import React from "react";
import { useImport } from "../contexts/ImportContext";
import { FileSearch, Link2, RefreshCw, CheckCircle2 } from "lucide-react";
import { Progress } from "./ui/progress";

export default function ImportProgressBar() {
  const { isProcessing, processingStatus, processingPhase, progress, PHASES } = useImport();

  if (!isProcessing) return null;

  // Calculate overall progress across all phases
  const phaseWeight = 100 / progress.totalPhases;
  const phaseProgress = progress.total > 0 ? (progress.current / progress.total) * phaseWeight : 0;
  const overallProgress = ((progress.phase - 1) * phaseWeight) + phaseProgress;

  // Phase styling
  const getPhaseInfo = () => {
    switch (processingPhase) {
      case PHASES.UPLOADING:
        return {
          icon: FileSearch,
          label: "Analyse IA",
          color: "from-blue-600 to-indigo-600",
          bgColor: "bg-blue-400"
        };
      case PHASES.RECONCILING:
        return {
          icon: Link2,
          label: "Rapprochement",
          color: "from-purple-600 to-pink-600",
          bgColor: "bg-purple-400"
        };
      case PHASES.SYNCING:
        return {
          icon: RefreshCw,
          label: "Synchronisation",
          color: "from-green-600 to-teal-600",
          bgColor: "bg-green-400"
        };
      case PHASES.DONE:
        return {
          icon: CheckCircle2,
          label: "Terminé",
          color: "from-green-500 to-emerald-500",
          bgColor: "bg-green-400"
        };
      default:
        return {
          icon: RefreshCw,
          label: "En cours",
          color: "from-blue-600 to-indigo-600",
          bgColor: "bg-blue-400"
        };
    }
  };

  const phaseInfo = getPhaseInfo();
  const Icon = phaseInfo.icon;

  return (
    <div className={`fixed top-0 left-[280px] right-0 z-[60] bg-gradient-to-r ${phaseInfo.color} text-white px-4 py-3 shadow-lg lg:left-[280px] max-lg:left-0 max-lg:top-14`}>
      <div className="max-w-5xl mx-auto">
        {/* Phase indicators */}
        <div className="flex items-center justify-center gap-6 mb-2">
          <PhaseIndicator 
            phase={1} 
            currentPhase={progress.phase} 
            label="Analyse IA" 
            icon={FileSearch}
          />
          <div className="w-8 h-0.5 bg-white/30" />
          <PhaseIndicator 
            phase={2} 
            currentPhase={progress.phase} 
            label="Rapprochement" 
            icon={Link2}
          />
          <div className="w-8 h-0.5 bg-white/30" />
          <PhaseIndicator 
            phase={3} 
            currentPhase={progress.phase} 
            label="Synchronisation" 
            icon={RefreshCw}
          />
        </div>

        {/* Current status */}
        <div className="flex items-center gap-4">
          <Icon className={`w-5 h-5 flex-shrink-0 ${processingPhase !== PHASES.DONE ? 'animate-spin' : ''}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="font-medium truncate text-sm">
                {processingStatus || "Traitement en cours..."}
              </p>
              {progress.total > 0 && (
                <span className="text-sm text-white/80 ml-2 whitespace-nowrap">
                  Étape {progress.phase}/3 • {progress.current}/{progress.total}
                </span>
              )}
            </div>
            <Progress value={overallProgress} className={`h-1.5 ${phaseInfo.bgColor}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function PhaseIndicator({ phase, currentPhase, label, icon: Icon }) {
  const isActive = phase === currentPhase;
  const isCompleted = phase < currentPhase;

  return (
    <div className={`flex items-center gap-1.5 transition-opacity ${
      isActive ? 'opacity-100' : isCompleted ? 'opacity-70' : 'opacity-40'
    }`}>
      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
        isCompleted ? 'bg-white/30' : isActive ? 'bg-white/20 ring-2 ring-white' : 'bg-white/10'
      }`}>
        {isCompleted ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : (
          <Icon className={`w-3.5 h-3.5 ${isActive ? 'animate-pulse' : ''}`} />
        )}
      </div>
      <span className={`text-xs font-medium hidden sm:inline ${isActive ? 'text-white' : 'text-white/70'}`}>
        {label}
      </span>
    </div>
  );
}
